import { ApolloClient } from '@apollo/client'
import { MyActiveFirmPreviewFeaturesQuery } from 'api/graphQL/queries/firm.queries'
import { PreviewFeature } from 'types/PreviewFeature'
import AuthEventService from './authEventService'

const SESSION_KEY = 'previewFeatures'

/**
 * Manages preview features with three-tier caching:
 * Memory → SessionStorage → API.
 * Cache auto-clears on logout for session isolation.
 */
export class PreviewFeaturesService {
    private static memoryCache: PreviewFeature[] | null = null
    private static initialized = false
    
    /** Initialize service and subscribe to auth events. */
    private static initialize(): void {
        if (this.initialized) return
        
        // Subscribe to logout events to clear cache
        AuthEventService.subscribe((event) => {
            if (event === 'logout') {
                this.clearCache()
            }
        })
        
        this.initialized = true
    }
    
    /**
     * Get features from cache or API.
     * @param apolloClient Apollo GraphQL client
     * @returns Enabled preview features
     */
    static async getFeatures(apolloClient: ApolloClient<any>): Promise<PreviewFeature[]> {
        // Initialize service on first use
        this.initialize()
        
        // Check memory first (fastest)
        if (this.memoryCache) {
            return this.memoryCache
        }
        
        // Check session storage (survives page refresh)
        const cached = sessionStorage.getItem(SESSION_KEY)
        if (cached) {
            try {
                const parsed = JSON.parse(cached)
                if (Array.isArray(parsed)) {
                    this.memoryCache = parsed as PreviewFeature[]
                    return this.memoryCache
                }
            } catch {
                // Clear corrupted cache
                sessionStorage.removeItem(SESSION_KEY)
            }
        }
        
        // Fetch from API (first load)
        try {
            const { data } = await apolloClient.query({
                query: MyActiveFirmPreviewFeaturesQuery,
                fetchPolicy: 'network-only',
            })
            
            const features = (data?.myActiveFirm?.previewFeatures || []) as PreviewFeature[]
            
            // Cache for this session
            this.memoryCache = features
            sessionStorage.setItem(SESSION_KEY, JSON.stringify(features))
            
            return features
        } catch (error) {
            console.error('Failed to fetch preview features:', error)
            return []
        }
    }
    
    /** Clear all cached features. */
    static clearCache(): void {
        this.memoryCache = null
        sessionStorage.removeItem(SESSION_KEY)
    }
    
    /**
     * Force fresh fetch from API.
     * @param apolloClient Apollo GraphQL client  
     * @returns Enabled preview features
     */
    static async forceRefresh(apolloClient: ApolloClient<any>): Promise<PreviewFeature[]> {
        this.clearCache()
        return this.getFeatures(apolloClient)
    }
    
    /** Reset service state (testing only). @internal */
    static resetForTesting(): void {
        if (process.env.NODE_ENV === 'test') {
            this.memoryCache = null
            this.initialized = false
            sessionStorage.removeItem(SESSION_KEY)
        }
    }
}